/**
 * Shared question renderer for live trivia and 1-v-1 duels.
 *
 * The prompt text + option grid are the only surfaces both observed
 * userscripts key off:
 *   - the DOM scraper grabs the prompt via a text scan / a fixed selector
 *     and then calls `querySelectorAll('button')` filtered by `A./B./C./D.`
 *     to auto-tap;
 *   - the OCR variant walks visible text via `innerText.length > 3` to
 *     compute a crop rect, forces `color:#fff` in `html2canvas`'s onclone,
 *     rasterizes, OCRs, and picks by `find(el => el.innerText.includes(x))`
 *     followed by `.click()`.
 *
 * Both attacks fail identically when the prompt and options live inside
 * a closed shadow root:
 *   - the shadow root reference is not exposed to page-context scripts;
 *     `host.shadowRoot` returns `null` for `mode: 'closed'`;
 *   - `querySelectorAll` on `document` does not pierce the shadow, so
 *     bots find zero buttons;
 *   - `html2canvas` walks the DOM and re-draws it (it is not a real
 *     screenshot). Closed roots are not traversed, so the OCR bot's
 *     crop is blank and it logs "No readable text";
 *   - synthetic `.click()` from outside would need a reference to a
 *     shadow-internal button; there is no way to obtain one.
 *
 * Everything OUTSIDE this component stays in light DOM: image, timer,
 * progress bar, 50/50 HUD, running score, mini-leaderboard, reveal-side
 * score line. Rationale: the bots depend on TEXT for both target
 * discovery and answer selection; hiding the image doesn't change their
 * behavior, and putting timer/HUDs in shadow would complicate the caller
 * for no anti-bot gain.
 *
 * Design notes for the caller:
 *   - Mount ONE view per game-question surface (one for live_room, one
 *     for duel_run). The controller returned from `mountQuestionView`
 *     is a stable handle; call its `.setQuestion`, `.setEliminated`,
 *     `.lockAnswer`, `.showReveal`, `.destroy` methods.
 *   - The view can be re-mounted safely on outer re-render — closed
 *     shadow roots are recreated with fresh state each mount. Any
 *     "in flight" reference from a torn-down mount is inert (its onSelect
 *     just calls the caller's stable closure).
 *   - Styles inside the shadow are hardcoded, not inherited from the
 *     page. This is the "nothing inherits in" hardening posture — a page
 *     stylesheet injection cannot restyle the shadow to make bot OCR
 *     easier (see the userscript's `color:#fff!important` onclone
 *     override — that override doesn't fire inside a closed shadow at
 *     all, but the defence-in-depth is intentional).
 */

import { markShadowOpenDetected } from './client_signals.js';

// Colors + font pulled from tokens.css / base.css. Hardcoded rather than
// referenced via var(--…) so an external stylesheet load has no effect on
// the shadow tree. If the design tokens change, update these in lockstep.
const S = {
  fontDisplay: "'Space Grotesk', 'Inter', system-ui, sans-serif",
  fontBody: "'Inter', system-ui, sans-serif",
  colorWhite: '#fff',
  colorMuted: '#8E89A8',
  colorSurf2: '#211C36',
  colorLine: '#2A2440',
  choicePickBg: 'rgba(233,75,162,0.22)',
  choicePickBorder: 'rgba(233,75,162,0.65)',
  choicePickTint: 'rgba(233,75,162,0.16)',
  choicePickTintBorder: 'rgba(233,75,162,0.55)',
  choiceCorrectBg: 'rgba(60,200,120,0.18)',
  choiceCorrectBorder: 'rgba(60,200,120,0.6)',
  choiceCorrectTick: '#5FE0BE',
};

/**
 * Escape a plain-text string for safe interpolation into HTML. We import
 * the caller's escape function through a passed-in `escape` helper rather
 * than adding a runtime dependency here, so this file has no coupling to
 * the rest of the app's imports.
 */
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build a stylesheet body that both `<style>`-tag and `CSSStyleSheet` paths
 * can share. Uses literals for every value that could otherwise inherit
 * from the page.
 *
 * `all: initial` on the container is deliberate — it wipes any cascade
 * that might have leaked in through user-agent defaults, so a userscript
 * that manages to reach the host cannot slip in a `!important` colour
 * override that survives into the tree. We then re-establish exactly
 * the typography we want, and nothing else.
 */
function buildStyles() {
  return `
    .qv-root {
      all: initial;
      display: block;
      font-family: ${S.fontBody};
      color: ${S.colorWhite};
    }
    .qv-prompt {
      font-family: ${S.fontBody};
      font-size: 15px;
      line-height: 1.45;
      color: ${S.colorWhite};
      margin: 0 0 14px 0;
      /* Preserve intentional line breaks in the question text (e.g. lyric
         questions, multi-line story prompts). Long single lines still wrap
         normally via the default word-wrap behaviour. */
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .qv-choices {
      display: grid;
      gap: 8px;
      grid-template-columns: 1fr;
    }
    .qv-choices.qv-two-col {
      grid-template-columns: repeat(2, 1fr);
    }
    .qv-choice {
      display: block;
      width: 100%;
      text-align: left;
      padding: 13px 16px;
      border-radius: 12px;
      background: ${S.colorSurf2};
      border: 1px solid ${S.colorLine};
      border-bottom: 3px solid ${S.colorLine};
      color: ${S.colorWhite};
      font-family: ${S.fontDisplay};
      font-weight: 600;
      font-size: 14.5px;
      cursor: pointer;
      transition: transform 90ms ease-out;
      /* Buttons that render into a 2-col grid still need to shrink
         gracefully; long option strings wrap rather than blowing the
         grid open. */
      overflow-wrap: anywhere;
    }
    .qv-choice:active {
      transform: translateY(1px);
      border-bottom-width: 2px;
    }
    .qv-choice:disabled {
      cursor: not-allowed;
    }
    .qv-choice-prefix {
      color: ${S.colorMuted};
      margin-right: 8px;
      font-weight: 700;
    }
    .qv-choice.qv-eliminated {
      background: rgba(255,255,255,0.02);
      border-color: ${S.colorLine};
      color: ${S.colorMuted};
      text-decoration: line-through;
      opacity: 0.45;
    }
    .qv-choice.qv-chosen {
      background: ${S.choicePickBg};
      border-color: ${S.choicePickBorder};
      opacity: 1;
    }
    .qv-choice.qv-locked-other {
      opacity: 0.55;
    }
    .qv-badge-right {
      float: right;
      font-size: 11px;
      font-weight: 600;
    }
    /* Reveal-phase choices are non-interactive divs (not buttons) —
       matches the existing visual language in live_room / duel_run and
       ensures a stray page-context script cannot re-arm them by removing
       the disabled attribute. */
    .qv-reveal-choice {
      display: block;
      padding: 11px 14px;
      border-radius: 10px;
      background: ${S.colorSurf2};
      border: 1px solid ${S.colorLine};
      color: ${S.colorWhite};
      font-size: 14px;
      font-family: ${S.fontBody};
    }
    .qv-reveal-choice.qv-correct {
      background: ${S.choiceCorrectBg};
      border-color: ${S.choiceCorrectBorder};
    }
    .qv-reveal-choice.qv-yours {
      background: ${S.choicePickTint};
      border-color: ${S.choicePickTintBorder};
    }
    .qv-tick { color: ${S.choiceCorrectTick}; }
    .qv-yours-lbl { opacity: 0.7; }
  `;
}

/**
 * Mount the question view into `host`. Returns a controller. The caller
 * is expected to hold on to the controller for the game's lifetime and
 * call its methods on state changes.
 *
 * Options:
 *   - onSelect(index): Called with the 0-based choice index when the
 *     user taps a choice during the answering phase. The caller decides
 *     what to do next (fire the answer POST, lock the view, etc.).
 *     Not invoked in reveal phase.
 *   - mode: 'live' or 'duel'. Currently only affects whether A/B/C/D
 *     prefixes are shown in front of the option text — duel_run always
 *     renders those; live_room does not. Preserving the visual language
 *     of each surface.
 */
export function mountQuestionView(host, { onSelect, mode = 'live' } = {}) {
  if (!(host instanceof Element)) {
    throw new Error('mountQuestionView: host must be an Element');
  }
  // If a prior view was mounted on this host and left behind a shadow
  // (mode: 'closed' means we can't inspect it), the browser silently
  // refuses to attach a second one. That branch shouldn't happen given
  // our caller pattern (fresh host per outer render), but we defend by
  // reusing an existing attached root when we can detect one.
  //
  // `host.shadowRoot` returns null for closed roots, so we can't check
  // that directly. Instead, tag the host — if we mounted it before, our
  // marker is present and we reuse the module-scoped controller.
  const existing = MOUNTED.get(host);
  if (existing) return existing;

  const shadow = host.attachShadow({ mode: 'closed' });
  // PR-B tamper canary. A userscript with `@run-at document-start` can
  // hijack `Element.prototype.attachShadow` to force `mode: 'open'`,
  // making `host.shadowRoot` non-null and reachable from page-context
  // scripts. Closed-mode is supposed to hide the root. If we see it
  // exposed, the tampering is confirmed — flag the session via
  // `markShadowOpenDetected` so every subsequent client_signals blob
  // carries `shadow_open_detected: true` for admin review. We do NOT
  // block the game: a broken user should still play, and the signal is
  // what matters.
  if (host.shadowRoot !== null) {
    markShadowOpenDetected();
  }
  const styleEl = document.createElement('style');
  styleEl.textContent = buildStyles();
  shadow.appendChild(styleEl);
  const rootEl = document.createElement('div');
  rootEl.className = 'qv-root';
  shadow.appendChild(rootEl);

  const state = {
    question: null,
    twoColumnLayout: false,
    locked: false,
    chosenIndex: null,
    eliminated: [],
    reveal: null, // { correctIndex, yourIndex } | null
  };

  const showPrefixes = mode === 'duel';

  function renderChoiceButton(text, i) {
    const classes = ['qv-choice'];
    const eliminated = state.eliminated.includes(i);
    if (state.locked) {
      if (state.chosenIndex === i) classes.push('qv-chosen');
      else classes.push('qv-locked-other');
    } else if (eliminated) {
      classes.push('qv-eliminated');
    }
    const isChosen = state.locked && state.chosenIndex === i;
    // Buttons after lock are disabled — matches the current UX and
    // means a subsequent `.click()` from an external script is a no-op
    // even inside a hypothetical shadow reference leak.
    const disabled = state.locked || (eliminated && !state.locked);
    const prefix = showPrefixes
      ? `<b class="qv-choice-prefix">${String.fromCharCode(65 + i)}</b>`
      : '';
    const badge = isChosen
      ? '<span class="qv-badge-right">✓ your pick</span>'
      : '';
    return (
      `<button type="button" class="${classes.join(' ')}" data-idx="${i}"` +
      (disabled ? ' disabled' : '') +
      `>${prefix}${esc(text)}${badge}</button>`
    );
  }

  function renderRevealChoice(text, i) {
    const { correctIndex, yourIndex } = state.reveal || {};
    const isCorrect = correctIndex === i;
    const wasYours = yourIndex === i;
    const classes = ['qv-reveal-choice'];
    if (isCorrect) classes.push('qv-correct');
    else if (wasYours) classes.push('qv-yours');
    const prefix = showPrefixes
      ? `<b class="qv-choice-prefix">${String.fromCharCode(65 + i)}</b>`
      : '';
    let badge = '';
    if (isCorrect) {
      badge = '<span class="qv-badge-right qv-tick">✓</span>';
    } else if (wasYours) {
      badge = '<span class="qv-badge-right qv-yours-lbl">your pick</span>';
    }
    return `<div class="${classes.join(' ')}">${prefix}${esc(text)}${badge}</div>`;
  }

  function render() {
    if (!state.question) {
      rootEl.innerHTML = '';
      return;
    }
    const q = state.question;
    const twoCol = state.twoColumnLayout ? ' qv-two-col' : '';
    const parts = [];
    parts.push(`<div class="qv-prompt">${esc(q.prompt)}</div>`);
    if (state.reveal) {
      parts.push(`<div class="qv-choices${twoCol}">`);
      for (let i = 0; i < q.choices.length; i++) {
        parts.push(renderRevealChoice(q.choices[i], i));
      }
      parts.push(`</div>`);
    } else {
      parts.push(`<div class="qv-choices${twoCol}">`);
      for (let i = 0; i < q.choices.length; i++) {
        parts.push(renderChoiceButton(q.choices[i], i));
      }
      parts.push(`</div>`);
    }
    rootEl.innerHTML = parts.join('');
  }

  // One delegated listener inside the shadow — cleaner than per-button
  // handlers, and it survives every re-render because we only replace
  // `rootEl.innerHTML`, not `rootEl` itself. The click event is passed
  // through to onSelect so the caller can inspect `isTrusted` for the
  // anti-cheat telemetry pass (see lib/client_signals.js). A page-context
  // synthetic `element.click()` — the observed userscripts' answer path —
  // arrives here with `event.isTrusted === false`. We do NOT gate the
  // callback on it: accessibility tools legitimately synthesise clicks,
  // and blocking those would break real players. It's recorded, not
  // enforced.
  rootEl.addEventListener('click', (ev) => {
    if (state.locked || state.reveal) return;
    const target = ev.target && ev.target.closest ? ev.target.closest('button[data-idx]') : null;
    if (!target) return;
    if (target.disabled) return;
    const idx = parseInt(target.getAttribute('data-idx'), 10);
    if (!Number.isFinite(idx)) return;
    if (typeof onSelect === 'function') onSelect(idx, ev);
  });

  // Keyboard support survives a closed shadow root — Tab navigates into
  // and out of it, buttons are focusable by default, Enter/Space fire
  // click. No extra wiring required.

  const controller = {
    /**
     * Set the current question. `prompt` and `choices` are the raw
     * server payload (per-user shuffled choices are preserved because
     * we render them in-order). `twoColumnLayout` mirrors the caller's
     * `!!q.image_url` decision so the button grid matches the outer
     * card layout.
     */
    setQuestion({ prompt, choices, twoColumnLayout }) {
      state.question = { prompt, choices: Array.isArray(choices) ? choices.slice() : [] };
      state.twoColumnLayout = !!twoColumnLayout;
      state.locked = false;
      state.chosenIndex = null;
      state.eliminated = [];
      state.reveal = null;
      render();
    },
    /**
     * Set the eliminated (50/50) indices for the current question.
     * Idempotent — safe to call multiple times, including on rehydrate
     * after a reconnect.
     */
    setEliminated(indices) {
      state.eliminated = Array.isArray(indices) ? indices.slice() : [];
      render();
    },
    /**
     * Lock the view on the chosen index. Used for optimistic-lock UX:
     * caller calls this the moment the user picks so the highlight
     * appears before the answer POST completes.
     */
    lockAnswer(chosenIndex) {
      state.locked = true;
      state.chosenIndex = chosenIndex;
      state.reveal = null;
      render();
    },
    /**
     * Enter reveal mode. Choices become non-interactive divs with
     * correct-green + your-pink highlighting. Idempotent.
     */
    showReveal({ correctIndex, yourIndex }) {
      state.reveal = {
        correctIndex: Number.isFinite(correctIndex) ? correctIndex : null,
        yourIndex: Number.isFinite(yourIndex) ? yourIndex : null,
      };
      state.locked = true;
      state.chosenIndex = Number.isFinite(yourIndex) ? yourIndex : state.chosenIndex;
      render();
    },
    /**
     * Detach the view. Removes the marker so a fresh mount can be
     * created on the same element later (e.g. after navigation).
     * The shadow root itself cannot be detached (browser limitation),
     * but blanking the internal container clears the visible tree.
     */
    destroy() {
      MOUNTED.delete(host);
      rootEl.innerHTML = '';
    },
  };

  MOUNTED.set(host, controller);
  return controller;
}

// Module-scoped map: host element → controller. WeakMap so hosts that
// leave the DOM don't keep controllers alive. This is the ONLY handle
// to a mounted shadow root from anywhere in the codebase; the closed
// shadow reference is never leaked into `window` or a returned object.
const MOUNTED = new WeakMap();


// ---------------------------------------------------------------------
// Decoy trap (PR 4)
// ---------------------------------------------------------------------
//
// The closed shadow root from `mountQuestionView` hides the REAL prompt
// + options from page-context scripts. This helper injects a fake
// prompt + fake options as LIGHT-DOM siblings of the host — the exact
// opposite trick: content that is visible to the userscripts' scan but
// invisible to humans.
//
// Why it works against both observed userscripts:
//   - The DOM-scraper variant runs `qs('button')` filtered by
//     `/^[A-D][\s.:]/`. In the new world with the shadow root, `qs`
//     on `document` returns zero real option buttons. But the decoy
//     buttons ARE in light DOM and match that regex — so the bot
//     finds them, sends the decoy text to its LLM, and calls
//     `element.click()` on the returned match. `.click()` fires the
//     button's own handler regardless of `pointer-events: none`
//     (that guard blocks POINTER events, not synthetic activation).
//   - The OCR variant walks `innerText.length > 3` to compute a crop
//     rect, then rasterises via `html2canvas` with an `onclone`
//     override of `* { color: #fff !important; background: #000
//     !important }`. Our decoy is `color: transparent` — invisible
//     in real rendering — but the onclone flips it to bright white
//     for OCR. The bot's LLM then answers based on decoy text, and
//     the subsequent `find(el => el.innerText.includes(answer))` hits
//     a decoy node again.
//
// The click handler on a decoy fires `onDecoyHit()` (which the caller
// wires to `client_signals.markDecoyHit`). It DOES NOT submit an
// answer — the real answer path is fully inside the closed shadow
// root, unreachable from a light-DOM click.
//
// Anti-fingerprinting:
//   - Prompt string, option strings, and DOM order are freshly
//     shuffled per mount from a pool. A bot can't filter by a fixed
//     text pattern.
//   - The wrapper's class is randomised at mount time so a bot can't
//     filter by class selector either.
//   - `pointer-events: none` + `tabindex="-1"` + `aria-hidden="true"`
//     keep the block completely inert to humans (no keyboard focus,
//     screen readers skip it, no visible layout impact since colour
//     is transparent and z-index sits behind the real content).

/**
 * Pool of plausibly-worded decoy options. Kept intentionally generic —
 * an OCR + LLM combo will happily "pick an answer" from these regardless
 * of the (invisible-to-the-bot) real question. Any option string that
 * gets clicked is a strong screen-scraping signal.
 */
const DECOY_OPTION_POOL = [
  'Lagos State',
  'Kaduna State',
  'The Niger River',
  'The River Benue',
  'Wole Soyinka',
  'Chinua Achebe',
  'Chimamanda Adichie',
  'Ben Enwonwu',
  'Independence Day 1960',
  'The Nok Culture',
  'The Sokoto Caliphate',
  'The Yoruba Empire',
  'Cassava Republic',
  'Farafina Books',
  'Kachifo Limited',
  'Bookcraft Africa',
  'Nnamdi Azikiwe',
  'Herbert Macaulay',
  'Tafawa Balewa',
  'Murtala Mohammed',
  'The Kwara Bank',
  'The Federal Palace',
  'Zuma Rock',
  'Aso Rock',
  'The Osun-Osogbo Grove',
  'The Sukur Cultural Landscape',
];

/**
 * Pool of decoy prompt lines. Reads like a plausible question stem.
 */
const DECOY_PROMPT_POOL = [
  'Which of the following is most commonly associated with this topic?',
  'The correct answer, according to most reference sources, is:',
  'Historians usually cite which of the following as the answer?',
  'The most widely-accepted answer to this question is:',
  'Standard references list the answer as:',
];

/**
 * Pick `n` distinct items from `pool`, in a fresh random order every
 * time. Uses `crypto.getRandomValues` when available so a page-context
 * bot can't predict the shuffle by seeding `Math.random`.
 */
function pickDistinct(pool, n) {
  const copy = pool.slice();
  const rand = () => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] / 0x100000000;
    }
    return Math.random();
  };
  // Fisher–Yates, capped at `n` swaps.
  for (let i = 0; i < n && i < copy.length; i++) {
    const j = i + Math.floor(rand() * (copy.length - i));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

function randomClassSuffix() {
  // 8 hex chars — enough entropy per mount that a bot can't filter by
  // class name across sessions.
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0].toString(16).padStart(8, '0');
  }
  return Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
}

/**
 * Mount a decoy block against `container`. The container must be
 * `position: relative` (or become so) — the decoy positions itself
 * absolutely inside it, behind the real content. Callers wrap their
 * question-view host in a positioned slot for exactly this.
 *
 * Options:
 *   - onDecoyHit: () => void. Fired the first time any decoy option is
 *     clicked. See `client_signals.markDecoyHit`.
 *
 * Returns `{ dispose() }` — removes the decoy block from the DOM.
 * Idempotent.
 */
export function mountDecoyBlock(container, { onDecoyHit } = {}) {
  if (!(container instanceof Element)) {
    throw new Error('mountDecoyBlock: container must be an Element');
  }
  const suffix = randomClassSuffix();
  const rootClass = `qv-decoy-${suffix}`;
  const optionClass = `qv-decoy-opt-${suffix}`;
  const prompts = DECOY_PROMPT_POOL;
  const promptIdx = Math.floor(Math.random() * prompts.length);
  const promptText = prompts[promptIdx];
  const options = pickDistinct(DECOY_OPTION_POOL, 4);

  const wrap = document.createElement('div');
  wrap.className = rootClass;
  wrap.setAttribute('aria-hidden', 'true');
  wrap.setAttribute('tabindex', '-1');
  // Inline styles chosen deliberately:
  //   - position: absolute + inset: 0 → occupy the same rect as the
  //     real question card so `innerText` bounding boxes contribute to
  //     the OCR crop rect the bot computes.
  //   - color: transparent → invisible to humans in real rendering;
  //     onclone `color:#fff!important` overrides it for OCR (which is
  //     the whole point).
  //   - pointer-events: none → real fingers pass THROUGH to the shadow
  //     host underneath. Synthetic `.click()` from a bot still fires
  //     the handler.
  //   - z-index: 0 (real content is at z-index 1) so if a browser ever
  //     ignores color, the decoy stays visually behind.
  //   - user-select: none → screen text-selection can't reach it.
  //   - overflow: hidden on the wrap → decoy children can't visually
  //     poke out of the card (bounding rect stays inside the crop).
  wrap.style.cssText = [
    'position: absolute',
    'inset: 0',
    'color: transparent',
    'pointer-events: none',
    'user-select: none',
    'z-index: 0',
    'overflow: hidden',
    // Match the padding of the enclosing card so element bounding
    // boxes align roughly with where the real prompt/options render.
    'padding: 14px',
  ].join('; ');

  // Fake prompt (browsable via document.querySelectorAll('div') filtered
  // by innerText.length > 3).
  const promptEl = document.createElement('div');
  promptEl.textContent = promptText;
  promptEl.style.cssText = 'font-size: 15px; margin-bottom: 14px;';
  wrap.appendChild(promptEl);

  // Fake options — <button> so the DOM-scraper regex `/^[A-D][\s.:]/`
  // hits them; each with a click handler that fires onDecoyHit. PR-B
  // polish: after a decoy is clicked, we tint it very slightly (still
  // fully transparent to humans) and add a `disabled` attribute. To a
  // bot that "checks for state change after click before retrying", the
  // decoy button LOOKS like it responded — the bot marks the answer
  // as submitted and stops trying, giving us one clean decoy_hit per
  // question instead of a runaway retry loop that hits every decoy.
  const optionsWrap = document.createElement('div');
  optionsWrap.style.cssText = 'display: block';
  for (let i = 0; i < options.length; i++) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = optionClass;
    btn.setAttribute('aria-hidden', 'true');
    btn.setAttribute('tabindex', '-1');
    // `A. …`, `B. …`, `C. …`, `D. …` — matches the userscripts' regex.
    btn.textContent = `${String.fromCharCode(65 + i)}. ${options[i]}`;
    btn.style.cssText = [
      'display: block',
      'width: 100%',
      'text-align: left',
      'padding: 13px 16px',
      'margin-bottom: 8px',
      // Same color/pointer trick as the wrapper — belt and braces.
      'color: transparent',
      'background: transparent',
      'border: 1px solid transparent',
      'pointer-events: none',
      'font-size: 14.5px',
    ].join('; ');
    btn.addEventListener('click', (ev) => {
      // Do NOT submit an answer. Do NOT surface any HUMAN-visible
      // feedback. But DO produce an artefact the bot can detect
      // ("this button looks like it responded"), so it doesn't
      // frantically click every decoy in the block.
      try { ev.preventDefault(); } catch { /* ignore */ }
      try {
        btn.setAttribute('disabled', 'disabled');
        // Change one style attribute — a tiny background/border tweak
        // that stays fully transparent to a human but is observable to
        // any script that snapshots computed style after clicking.
        btn.style.background = 'rgba(0,0,0,0.001)';
        btn.style.borderColor = 'rgba(0,0,0,0.001)';
      } catch { /* noop */ }
      if (typeof onDecoyHit === 'function') onDecoyHit();
    });
    optionsWrap.appendChild(btn);
  }
  wrap.appendChild(optionsWrap);

  container.appendChild(wrap);

  return {
    dispose() {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    },
  };
}
